import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AdminLogin from "../../authentication/components/AdminLogin.js";
import CustomerLogin from "../../authentication/components/CustomerLogin.js";
import Login from "../../authentication/pages/Login";
import Signup from "../../authentication/pages/Signup";
import SSOLogin from "../../authentication/pages/SSOLogin";
import OAuthCallback from "../../authentication/pages/OAuthCallback";
import ForgotPasswordPage from "../../authentication/pages/ForgotPassword";
import ForgotPasswordPageNew from "../../authentication/pages/ForgotPasswordNew";
import VerificationPage from "../../authentication/pages/VerificationPage";
import OTPPage from "../../authentication/pages/OTP";
import OTPPageNew from "../../authentication/pages/OTPNew";
import ResetPasswordPage from "../../authentication/pages/ResetPassword";
import Dashboard from "../../components/Dashboard/Dashboard";
import AuthLayout from "../../layout/auth-layout/AuthLayout";
import Home from "../../pages/Home";
import InitialPage from "../../pages/InitialPage";
import LandingPage from "../../pages/LandingPage";
import TestingMonacoPage from "../../pages/TestingMonacoPage";
import ContactUsPage from "../../pages/ContactUs";
import ProtectedCompanyProfile from "../../pages/ProtectedCompanyProfile";
import NotFound from "../../pages/NotFound";
import { RouteHistoryProvider } from "../../store/context/admin/route-history-context.js";
import AdminProtectedLayout from "../../utils/routes/AdminProtectedLayout";
import ProtectedLayout from "../../utils/routes/ProtectedLayout";
import UltimateAgentProtectedLayout from "../../utils/routes/UltimateAgentProtectedLayout";
import LoadingPage from "../common/Loader";
import OtpVerificationPage from "../otp-verification/OtpVerification";
import {
  ProtectedAdminRoutes,
  ProtectedSharedRoutes,
  ProtectedUserRoutes,
} from "./ProtectedInnerRoutes";
import SharedProductRoutes from "./SharedProductRoutes";
import { getSessionData } from "../../utils/interceptors/localStorageService.js";
import { SelfUserClassificationEnum } from "../constants/constants.js";

const Privacy = lazy(() => import("../../pages/TermsAndPrivacy/Privacy"));
const Terms = lazy(() => import("../../pages/TermsAndPrivacy/Terms"));
const Authentication = lazy(() => import("../../authentication/pages/Auth"));
const ValidateUser = lazy(
  () =>
    import("../../pages/Settings And More/Corporate Settings/ValidateUser.js"),
);
const ValidatorUserQuestions = lazy(
  () =>
    import("../../pages/Settings And More/Corporate Settings/SecurityFeatures.js"),
);
const RequestApprovalPage = lazy(
  () => import("../../pages/RequestApproval/RequestApprovalPageV2"),
);
const RequestApprovalTestPage = lazy(
  () => import("../../pages/RequestApproval/RequestApprovalTestPage"),
);
const ReceiptGeneratorPage = lazy(
  () => import("../../pages/ReceiptGeneratorPage"),
);
const NibbsIAgreeCallbackPage = lazy(
  () => import("../../pages/Callbacks/NibbsIAgreeCallback"),
);
const NibbsIAgreeFramePage = lazy(
  () => import("../../pages/Callbacks/NibbsIAgreeFrame"),
);
const EntityProcessor = lazy(() => import("../../pages/EntityProcessor"));
const UADashboard = lazy(() => import("../../pages/UltimateAgent/UltimateAgentDashboard"));
const UAVerificationPackages = lazy(() => import("../../pages/UltimateAgent/VerificationPackages"));
const UAApplications = lazy(() => import("../../pages/UltimateAgent/Applications"));
const UAProperties = lazy(() => import("../../pages/UltimateAgent/Properties"));
const UAContracts  = lazy(() => import("../../pages/UltimateAgent/Contracts"));
const UATenancies  = lazy(() => import("../../pages/UltimateAgent/Tenancies"));
const UATeamRoles  = lazy(() => import("../../pages/UltimateAgent/TeamRoles"));

const AppRoutes = () => {
  const sessionData = getSessionData();
  const isAdmin =
    sessionData?.userClassification ===
    SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value;

  console.log("isAdmin?? ", isAdmin);
  return (
    <RouteHistoryProvider>
      {/* <Suspense fallback={<LoadingPage />}> */}
      <Routes>
        {/* Landing Page Routes */}
        <Route
          path="/landing"
          element={
            <Suspense fallback={<LoadingPage />}>
              <LandingPage />
            </Suspense>
          }
        />
        {/* <Route path="/testing-monaco" element={<TestingMonacoPage />} /> */}
        <Route
          path="/company-profile"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ProtectedCompanyProfile />
            </Suspense>
          }
        />
        <Route
          path="/segment"
          element={
            <Suspense fallback={<LoadingPage />}>
              <InitialPage />
            </Suspense>
          }
        />
        <Route
          path="/entity-processor/:uuid"
          element={
            <Suspense fallback={<LoadingPage />}>
              <EntityProcessor />
            </Suspense>
          }
        />

        {/* PortalLayout path */}
        <Route path="/" element={<ProtectedLayout element={Home} />} />
        <Route path="/products" element={<ProtectedLayout element={Home} />} />

        {/* InnerLayout path */}
        <Route
          path="/dashboard/*"
          element={<ProtectedLayout element={Dashboard} />}
        />

        {/* Shared Routes between InnerLayout and PortalLayout */}
        {SharedProductRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={<ProtectedLayout element={route.element} />}
          />
        ))}

        {/* UltimateAgent Module — own sidebar, bypasses InnerLayout */}
        <Route path="/ultimate-agent" element={<UltimateAgentProtectedLayout />}>
          <Route index element={<Suspense fallback={<LoadingPage />}><UADashboard /></Suspense>} />
          <Route path="verifications" element={<Suspense fallback={<LoadingPage />}><UAVerificationPackages /></Suspense>} />
          <Route path="applications" element={<Suspense fallback={<LoadingPage />}><UAApplications /></Suspense>} />
          <Route path="properties" element={<Suspense fallback={<LoadingPage />}><UAProperties /></Suspense>} />
          <Route path="contracts"   element={<Suspense fallback={<LoadingPage />}><UAContracts /></Suspense>} />
          <Route path="tenancies"   element={<Suspense fallback={<LoadingPage />}><UATenancies /></Suspense>} />
          <Route path="team"        element={<Suspense fallback={<LoadingPage />}><UATeamRoles /></Suspense>} />
          <Route path="*" element={<Suspense fallback={<LoadingPage />}><UADashboard /></Suspense>} />
        </Route>

        {/* Protected Inner Routes*/}
        {ProtectedUserRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={<ProtectedLayout element={route.element} />}
          />
        ))}

        {/* Protected Admin Routes */}
        {ProtectedAdminRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={<AdminProtectedLayout element={route.element} />}
          />
        ))}
        {/* Protected Shared Routes*/}
        {ProtectedSharedRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={
              isAdmin ? (
                <AdminProtectedLayout element={route.element} />
              ) : (
                <ProtectedLayout element={route.element} />
              )
            }
          />
        ))}

        {/* New Professional Auth Routes (Most specific first) */}
        {/* SSO Login Routes */}
        <Route
          path="/sso-login"
          element={
            <Suspense fallback={<LoadingPage />}>
              <SSOLogin />
            </Suspense>
          }
        />
        <Route
          path="/admin/sso-login"
          element={
            <Suspense fallback={<LoadingPage />}>
              <SSOLogin />
            </Suspense>
          }
        />
        <Route
          path="/auth/callback"
          element={
            <Suspense fallback={<LoadingPage />}>
              <OAuthCallback />
            </Suspense>
          }
        />

        {/* Traditional Login Routes */}
        <Route
          path="/authentication/login"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/authentication/admin/login"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/authentication/otp"
          element={
            <Suspense fallback={<LoadingPage />}>
              <OTPPageNew />
            </Suspense>
          }
        />
        <Route
          path="/authentication/verification"
          element={
            <Suspense fallback={<LoadingPage />}>
              <VerificationPage />
            </Suspense>
          }
        />
        <Route
          path="/authentication/forgot-password-new"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ForgotPasswordPageNew />
            </Suspense>
          }
        />

        {/* New Unified Authentication with mode parameter (for ?mode=signup or ?mode=signin) */}
        <Route
          path="/authentication"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Authentication />
            </Suspense>
          }
        />

        {/* Old Signup Route (keeping for backward compatibility but using old Signup) */}
        <Route
          path="/authentication/signup"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Signup />
            </Suspense>
          }
        />

        {/* Legacy Auth Routes with AuthLayout wrapper (catch remaining /authentication/* routes) */}
        <Route
          path="/authentication/*"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AuthRoutes />
            </Suspense>
          }
        />

        {/* Public Pages */}
        <Route
          path="/contact"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ContactUsPage />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Terms />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Privacy />
            </Suspense>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ResetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/forgot-password/otp-verify"
          element={
            <Suspense fallback={<LoadingPage />}>
              <OtpVerificationPage />
            </Suspense>
          }
        />

        {/* ✅ Request Approval - Public/Unprotected Route */}
        {/* Works for BOTH authenticated and unauthenticated users */}
        {/* - Authenticated: Shows InnerLayout with sidebar (handled internally) */}
        {/* - Unauthenticated: Shows clean standalone layout (no sidebar) */}
        {/* - URL: /request-approval/:formId?token=xxx */}
        {/* - Uses formId from URL params and token from query params */}
        {/* - Component: RequestApprovalPageV2 (with RuntimeFormViewer) */}
        <Route
          path="/request-approval/:formId"
          element={
            <Suspense fallback={<LoadingPage />}>
              <RequestApprovalPage />
            </Suspense>
          }
        />

        {/* Test page for Request Approval - Development/Testing only */}
        <Route
          path="/test/request-approval"
          element={
            <Suspense fallback={<LoadingPage />}>
              <RequestApprovalTestPage />
            </Suspense>
          }
        />

        {/* ✅ Receipt Generator - Public/Unprotected Route (No authentication required) */}
        {/* URL: /receipt-generator */}
        {/* Create professional receipts and download as PDF */}
        <Route
          path="/receipt-generator"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ReceiptGeneratorPage />
            </Suspense>
          }
        />

        {/* ✅ NIBBS iAgree callback - Public/Unprotected Route (No authentication required) */}
        <Route
          path="/igree"
          element={
            <Suspense fallback={<LoadingPage />}>
              <NibbsIAgreeFramePage />
            </Suspense>
          }
        />
        <Route
          path="/iagree"
          element={
            <Suspense fallback={<LoadingPage />}>
              <NibbsIAgreeFramePage />
            </Suspense>
          }
        />
        <Route
          path="/call-backs/nibbs/iagree/*"
          element={
            <Suspense fallback={<LoadingPage />}>
              <NibbsIAgreeCallbackPage />
            </Suspense>
          }
        />
        {/* Alias (common misspelling) */}
        <Route
          path="/call-backs/nibss/iagree/*"
          element={
            <Suspense fallback={<LoadingPage />}>
              <NibbsIAgreeCallbackPage />
            </Suspense>
          }
        />

        <Route
          path="/admin"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Navigate to="/authentication/admin/login" replace={true} />
            </Suspense>
          }
        />

        {/* 404 Not Found Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* </Suspense> */}
    </RouteHistoryProvider>
  );
};

const AuthRoutes = () => {
  return (
    <AuthLayout>
      <Suspense
        fallback={
          <div>
            <LoadingPage />
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<Authentication />} />
          <Route path="/otp" element={<OTPPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/validate-user" element={<ValidateUser />} />
          <Route
            path="/validate-user/security-features"
            element={<ValidatorUserQuestions />}
          />
          {/* Legacy Login Routes (for backward compatibility) */}
          <Route path="/login-old" element={<CustomerLogin />} />
          <Route path="/admin/login-old" element={<AdminLogin />} />
          <Route path="/signup-old" element={<InitialPage />} />
          {/* <Route path="/signup" element={<Navigate to="/segment" />} /> */}
        </Routes>
      </Suspense>
    </AuthLayout>
  );
};

export default AppRoutes;
